import type { LocalizedDict } from './index'
import type { AgeGroup, EtsyResearchErrorCode, SearchKind } from '../api/etsy-research'

export type EtsyResearchStrings = {
  title: string
  description: string
  teamOnly: { title: string; body: string }
  tabs: {
    label: string
    keyword: string
    shop: string
    reviews: string
    listing: string
    today: string
  }
  usage: (used: number, limit: number) => string
  usageHint: string
  credits: (n: string) => string
  repeatFree: string
  cached: string
  search: string
  searching: string
  errors: Record<EtsyResearchErrorCode, string> & { generic: string }
  pendingRetry: (seconds: number) => string
  ageGroups: Record<AgeGroup, string>
  ageFilter: string
  allAges: string
  openOnEtsy: string
  inspect: string
  dash: string
  days: (n: string) => string
  estimateNote: string
  history: {
    title: string
    sales: string
    revenue: string
    none: string
    locked: string
    note: string
  }
  table: {
    caption: (n: number) => string
    position: string
    listing: string
    found: string
    age: string
    favorites: string
    reviews: string
    monthly: string
    lastMonth: string
    trend: string
    price: string
    noRows: string
  }
  keyword: {
    placeholder: string
    label: string
    emptyTitle: string
    emptyBody: string
    metrics: {
      etsyMonthly: string
      googleMonthly: string
      competition: string
      competing: string
      change3m: string
      changeYoy: string
      avgPrice: string
    }
    competitionLevels: Record<string, string>
    summary: (shown: number, total: string) => string
    shops: string
    shopsHint: string
    shopListings: (n: number) => string
    inspectShop: string
  }
  today: {
    emptyTitle: string
    emptyBody: string
    searches: string
    searchListings: (n: number) => string
    free: string
    kinds: Record<SearchKind, string>
  }
  shop: {
    placeholder: string
    label: string
    emptyTitle: string
    emptyBody: string
    openedYears: (years: number, months: number) => string
    starSeller: string
    metrics: {
      totalSales: string
      monthlySales: string
      lastMonth: string
      lastMonthRevenue: string
      trend: string
      reviews: string
      rating: string
      activeListings: string
      avgPrice: string
      favorites: string
    }
    bestSellers: (n: number) => string
  }
  reviews: {
    placeholder: string
    label: string
    emptyTitle: string
    emptyBody: string
    metrics: { total: string; average: string; withImages: string }
    pending: string
    ratings: string
    stars: (n: number) => string
    newest: (n: number) => string
    noText: string
    top: string
    topNote: string
    reviewCount: (n: number) => string
  }
  listing: {
    placeholder: string
    label: string
    emptyTitle: string
    emptyBody: string
    shop: string
    listed: string
    age: string
    ageGroup: string
    price: string
    category: string
    kind: string
    digital: string
    physical: string
    personalizable: string
    materials: string
    metrics: {
      monthly: string
      lastMonth: string
      trend: string
      monthlyRevenue: string
      lifetime: string
      favorites: string
      reviews: string
    }
    tags: (n: number) => string
    tagsHint: string
    copyAll: string
    copySelected: (n: number) => string
    copied: string
    copyFailed: string
    images: (n: number) => string
  }
}

const EN: EtsyResearchStrings = {
  title: 'Etsy research',
  description:
    "Look inside any Etsy shop, listing or search phrase with Alura's sales estimates. PEXIFY team only.",
  teamOnly: {
    title: 'Etsy research is for the PEXIFY team',
    body: "The research data comes from Alura, whose terms let us use it inside our own team only. Your PEXIFY contact can look something up for you.",
  },
  tabs: {
    label: 'Etsy research',
    keyword: 'Keyword',
    shop: 'Shop',
    reviews: 'Shop reviews',
    listing: 'Listing',
    today: "Today's opportunities",
  },
  usage: (used, limit) => `Today ${used} / ${limit} searches`,
  usageHint: 'Each new search uses one. The count resets at midnight UTC.',
  credits: (n) => `${n} Alura credits left`,
  repeatFree: 'Searching the same thing again today is free and uses no Alura credits.',
  cached: "From today's earlier search — no search or credits used.",
  search: 'Search',
  searching: 'Searching…',
  errors: {
    daily_limit: "Today's searches are used up. They come back at midnight UTC.",
    not_found: 'Alura has no such shop, listing or keyword.',
    invalid_query: 'That does not look like what this tab searches for.',
    alura_pending: 'Alura is fetching this from Etsy right now.',
    alura_not_configured: 'No working Alura key yet. Add it under Admin Settings · Research tools.',
    alura_credits: "Alura's credits are used up for now. Try later, or raise the Alura plan.",
    alura_plan: "This needs a higher Alura plan.",
    alura_unavailable: 'Alura could not be read right now.',
    generic: 'The search did not work.',
  },
  pendingRetry: (s) => `Search again in about ${s} seconds — it is free until the answer arrives.`,
  ageGroups: { a: '0–30 days', b: '31–60 days', c: '61–90 days', d: '91–180 days', e: '181+ days' },
  ageFilter: 'Listing age',
  allAges: 'All',
  openOnEtsy: 'Open on Etsy',
  inspect: 'Look inside this listing',
  dash: '—',
  days: (n) => `${n} d`,
  estimateNote:
    "Sales, revenue and trend are Alura's estimates, not Etsy figures; a shop's total sales is Etsy's own count.",
  history: {
    title: 'Month by month',
    sales: 'Est. sales',
    revenue: 'Est. revenue',
    none: 'Alura has no monthly history for this yet.',
    locked: "Month-by-month history needs Alura's Growth plan. Everything else on this page works on the current plan.",
    note: 'Completed months only. An empty month means Alura does not know it.',
  },
  table: {
    caption: (n) => `${n} listings`,
    position: 'Rank',
    listing: 'Listing',
    found: 'Found in',
    age: 'Age',
    favorites: 'Favourites',
    reviews: 'Reviews',
    monthly: 'Avg / month',
    lastMonth: 'Last month',
    trend: '3-month trend',
    price: 'Price',
    noRows: 'No listings in this age group.',
  },
  keyword: {
    placeholder: 'wooden watch box',
    label: 'Search phrase',
    emptyTitle: 'Check a search phrase',
    emptyBody: "How often buyers search it on Etsy, how crowded it is, which way it is going, and the listings that rank for it. Uses 11 Alura credits.",
    metrics: {
      etsyMonthly: 'Etsy searches / month',
      googleMonthly: 'Google searches / month',
      competition: 'Competition',
      competing: 'Competing listings',
      change3m: 'Change, 3 months',
      changeYoy: 'Change, 1 year',
      avgPrice: 'Average price',
    },
    competitionLevels: { low: 'Low', medium: 'Medium', high: 'High', very_high: 'Very high' },
    summary: (shown, total) => `The ${shown} top-ranked listings of ${total} for this phrase.`,
    shops: 'Shops in the top results',
    shopsHint: 'Shops with the most top-ranked listings',
    shopListings: (n) => `${n} listing${n === 1 ? '' : 's'}`,
    inspectShop: 'Look inside this shop',
  },
  today: {
    emptyTitle: 'No searches yet today',
    emptyBody: "Every listing found by today's keyword and shop searches collects here.",
    searches: "Today's searches",
    searchListings: (n) => `${n} listing${n === 1 ? '' : 's'}`,
    free: 'This page uses no searches or credits.',
    kinds: { keyword: 'Keyword', shop: 'Shop' },
  },
  shop: {
    placeholder: 'OakAndEmberCo or etsy.com/shop/OakAndEmberCo',
    label: 'Shop name, number or link',
    emptyTitle: 'Look inside a shop',
    emptyBody: 'Total sales, estimated sales and revenue a month, trend, its 25 best sellers and month-by-month history. Uses 3 Alura credits.',
    openedYears: (y, m) => (y > 0 ? `open ${y} yr ${m} mo` : `open ${m} mo`),
    starSeller: 'Star Seller',
    metrics: {
      totalSales: 'Total sales',
      monthlySales: 'Est. sales / month',
      lastMonth: 'Est. sales last month',
      lastMonthRevenue: 'Est. revenue last month',
      trend: '3-month trend',
      reviews: 'Reviews',
      rating: 'Rating',
      activeListings: 'Active listings',
      avgPrice: 'Average price',
      favorites: 'Favourites',
    },
    bestSellers: (n) => `Best sellers (${n})`,
  },
  reviews: {
    placeholder: 'OakAndEmberCo',
    label: 'Shop name, number or link',
    emptyTitle: "Read a shop's reviews",
    emptyBody: 'The star split of all its reviews and the 25 newest, with the listing each one is about. Uses 2 Alura credits.',
    metrics: { total: 'Reviews', average: 'Average', withImages: 'With photos' },
    pending: 'Alura is still counting this shop’s reviews — search again in a minute for the totals.',
    ratings: 'Stars, all reviews',
    stars: (n) => `${n}★`,
    newest: (n) => `Newest ${n} reviews`,
    noText: 'No text',
    top: 'Most reviewed in the newest reviews',
    topNote: 'Counted over the newest reviews only.',
    reviewCount: (n) => `${n} review${n === 1 ? '' : 's'}`,
  },
  listing: {
    placeholder: 'etsy.com/listing/4354726818 or 4354726818',
    label: 'Listing link or number',
    emptyTitle: 'Look inside a listing',
    emptyBody: 'Estimated sales and revenue, trend, tags, photos and month-by-month history. Uses 2 Alura credits.',
    shop: 'Shop',
    listed: 'Listed',
    age: 'Age',
    ageGroup: 'Age group',
    price: 'Price',
    category: 'Category',
    kind: 'Kind',
    digital: 'Digital',
    physical: 'Physical',
    personalizable: 'Personalizable',
    materials: 'Materials',
    metrics: {
      monthly: 'Est. sales / month',
      lastMonth: 'Est. sales last month',
      trend: '3-month trend',
      monthlyRevenue: 'Est. revenue / month',
      lifetime: 'Est. sales, all time',
      favorites: 'Favourites',
      reviews: 'Reviews',
    },
    tags: (n) => `Tags (${n})`,
    tagsHint: 'Click a tag to copy it, or tick several and copy them together.',
    copyAll: 'Copy all',
    copySelected: (n) => `Copy selected (${n})`,
    copied: 'Copied',
    copyFailed: 'Could not copy',
    images: (n) => `${n} photos`,
  },
}

const TR: EtsyResearchStrings = {
  title: 'Etsy araştırma',
  description:
    'Herhangi bir Etsy mağazasının, ilanının veya arama kelimesinin içine Alura satış tahminleriyle bakın. Yalnız PEXIFY ekibi.',
  teamOnly: {
    title: 'Etsy araştırma PEXIFY ekibine özel',
    body: 'Araştırma verisi Alura’dan geliyor; Alura’nın şartları bu veriyi yalnız kendi ekibimizde kullanmamıza izin veriyor. PEXIFY’daki irtibat kişiniz sizin için bakabilir.',
  },
  tabs: {
    label: 'Etsy araştırma',
    keyword: 'Anahtar kelime',
    shop: 'Mağaza',
    reviews: 'Mağaza yorumları',
    listing: 'İlan',
    today: 'Bugünün fırsatları',
  },
  usage: (used, limit) => `Bugün ${used} / ${limit} arama`,
  usageHint: 'Her yeni arama bir hak kullanır. Sayaç UTC gece yarısı sıfırlanır.',
  credits: (n) => `${n} Alura kredisi kaldı`,
  repeatFree: 'Aynı şeyi bugün tekrar aramak hak düşürmez, Alura kredisi harcamaz.',
  cached: 'Bugünkü önceki aramadan — hak ve kredi harcanmadı.',
  search: 'Ara',
  searching: 'Aranıyor…',
  errors: {
    daily_limit: 'Bugünkü arama hakları bitti. UTC gece yarısı yenilenir.',
    not_found: 'Alura’da böyle bir mağaza, ilan veya kelime yok.',
    invalid_query: 'Bu, bu sekmenin aradığı şeye benzemiyor.',
    alura_pending: 'Alura bunu şu an Etsy’den getiriyor.',
    alura_not_configured: 'Henüz çalışan bir Alura anahtarı yok. Admin Ayarlar · Araştırma araçları’ndan ekleyin.',
    alura_credits: 'Alura kredileri şimdilik bitti. Sonra deneyin ya da Alura planını yükseltin.',
    alura_plan: 'Bunun için daha yüksek bir Alura planı gerekiyor.',
    alura_unavailable: 'Alura şu an okunamadı.',
    generic: 'Arama çalışmadı.',
  },
  pendingRetry: (s) => `Yaklaşık ${s} saniye sonra tekrar arayın — cevap gelene kadar ücretsiz.`,
  ageGroups: { a: '0–30 gün', b: '31–60 gün', c: '61–90 gün', d: '91–180 gün', e: '181+ gün' },
  ageFilter: 'İlan yaşı',
  allAges: 'Tümü',
  openOnEtsy: 'Etsy’de aç',
  inspect: 'Bu ilanın içine bak',
  dash: '—',
  days: (n) => `${n} g`,
  estimateNote:
    'Satış, gelir ve eğilim Alura’nın tahminidir, Etsy rakamı değildir; mağazanın toplam satışı Etsy’nin kendi sayısıdır.',
  history: {
    title: 'Ay ay',
    sales: 'Tahmini satış',
    revenue: 'Tahmini gelir',
    none: 'Alura’da bunun için henüz aylık geçmiş yok.',
    locked: 'Ay ay geçmiş için Alura’nın Growth planı gerekiyor. Bu sayfadaki diğer her şey mevcut planla çalışır.',
    note: 'Yalnız tamamlanmış aylar. Boş ay, Alura’nın o ayı bilmediği anlamına gelir.',
  },
  table: {
    caption: (n) => `${n} ilan`,
    position: 'Sıra',
    listing: 'İlan',
    found: 'Bulunduğu arama',
    age: 'Yaş',
    favorites: 'Favori',
    reviews: 'Yorum',
    monthly: 'Ort. / ay',
    lastMonth: 'Geçen ay',
    trend: '3 aylık eğilim',
    price: 'Fiyat',
    noRows: 'Bu yaş grubunda ilan yok.',
  },
  keyword: {
    placeholder: 'wooden watch box',
    label: 'Arama kelimesi',
    emptyTitle: 'Bir arama kelimesine bakın',
    emptyBody: 'Etsy’de ayda ne kadar aranıyor, rekabet ne kadar, hangi yöne gidiyor ve bu kelimede üst sıradaki ilanlar. 11 Alura kredisi harcar.',
    metrics: {
      etsyMonthly: 'Etsy araması / ay',
      googleMonthly: 'Google araması / ay',
      competition: 'Rekabet',
      competing: 'Rakip ilan',
      change3m: 'Değişim, 3 ay',
      changeYoy: 'Değişim, 1 yıl',
      avgPrice: 'Ortalama fiyat',
    },
    competitionLevels: { low: 'Düşük', medium: 'Orta', high: 'Yüksek', very_high: 'Çok yüksek' },
    summary: (shown, total) => `Bu kelimenin ${total} sonucundan en üstteki ${shown} ilan.`,
    shops: 'Üst sonuçlardaki mağazalar',
    shopsHint: 'Üst sıralarda en çok ilanı olan mağazalar',
    shopListings: (n) => `${n} ilan`,
    inspectShop: 'Bu mağazanın içine bak',
  },
  today: {
    emptyTitle: 'Bugün henüz arama yok',
    emptyBody: 'Bugünkü anahtar kelime ve mağaza aramalarında bulunan her ilan burada toplanır.',
    searches: 'Bugünkü aramalar',
    searchListings: (n) => `${n} ilan`,
    free: 'Bu sayfa arama hakkı ve kredi harcamaz.',
    kinds: { keyword: 'Kelime', shop: 'Mağaza' },
  },
  shop: {
    placeholder: 'OakAndEmberCo veya etsy.com/shop/OakAndEmberCo',
    label: 'Mağaza adı, numarası veya linki',
    emptyTitle: 'Bir mağazanın içine bakın',
    emptyBody: 'Toplam satış, aylık tahmini satış ve gelir, eğilim, en çok satan 25 ürünü ve ay ay geçmişi. 3 Alura kredisi harcar.',
    openedYears: (y, m) => (y > 0 ? `${y} yıl ${m} aydır açık` : `${m} aydır açık`),
    starSeller: 'Yıldız Satıcı',
    metrics: {
      totalSales: 'Toplam satış',
      monthlySales: 'Tahmini satış / ay',
      lastMonth: 'Geçen ay tahmini satış',
      lastMonthRevenue: 'Geçen ay tahmini gelir',
      trend: '3 aylık eğilim',
      reviews: 'Yorum',
      rating: 'Puan',
      activeListings: 'Aktif ilan',
      avgPrice: 'Ortalama fiyat',
      favorites: 'Favori',
    },
    bestSellers: (n) => `En çok satanlar (${n})`,
  },
  reviews: {
    placeholder: 'OakAndEmberCo',
    label: 'Mağaza adı, numarası veya linki',
    emptyTitle: 'Bir mağazanın yorumlarını okuyun',
    emptyBody: 'Tüm yorumların yıldız dağılımı ve en yeni 25 yorum, her birinin hangi ilan için olduğuyla. 2 Alura kredisi harcar.',
    metrics: { total: 'Yorum', average: 'Ortalama', withImages: 'Fotoğraflı' },
    pending: 'Alura bu mağazanın yorumlarını hâlâ sayıyor — toplamlar için bir dakika sonra tekrar arayın.',
    ratings: 'Yıldızlar, tüm yorumlar',
    stars: (n) => `${n}★`,
    newest: (n) => `En yeni ${n} yorum`,
    noText: 'Metin yok',
    top: 'En yeni yorumlarda en çok yorum alanlar',
    topNote: 'Yalnız en yeni yorumlar üzerinden sayılır.',
    reviewCount: (n) => `${n} yorum`,
  },
  listing: {
    placeholder: 'etsy.com/listing/4354726818 veya 4354726818',
    label: 'İlan linki veya numarası',
    emptyTitle: 'Bir ilanın içine bakın',
    emptyBody: 'Tahmini satış ve gelir, eğilim, etiketler, fotoğraflar ve ay ay geçmiş. 2 Alura kredisi harcar.',
    shop: 'Mağaza',
    listed: 'Yayına giriş',
    age: 'Yaş',
    ageGroup: 'Yaş grubu',
    price: 'Fiyat',
    category: 'Kategori',
    kind: 'Tür',
    digital: 'Dijital',
    physical: 'Fiziksel',
    personalizable: 'Kişiselleştirilebilir',
    materials: 'Malzemeler',
    metrics: {
      monthly: 'Tahmini satış / ay',
      lastMonth: 'Geçen ay tahmini satış',
      trend: '3 aylık eğilim',
      monthlyRevenue: 'Tahmini gelir / ay',
      lifetime: 'Tahmini satış, toplam',
      favorites: 'Favori',
      reviews: 'Yorum',
    },
    tags: (n) => `Etiketler (${n})`,
    tagsHint: 'Kopyalamak için etikete tıklayın veya birkaçını işaretleyip birlikte kopyalayın.',
    copyAll: 'Hepsini kopyala',
    copySelected: (n) => `Seçilenleri kopyala (${n})`,
    copied: 'Kopyalandı',
    copyFailed: 'Kopyalanamadı',
    images: (n) => `${n} fotoğraf`,
  },
}

export const ETSY_RESEARCH_STRINGS: LocalizedDict<EtsyResearchStrings> = { en: EN, tr: TR }
